import { useState, useRef, useCallback } from 'react';

export type AnnouncementType = 'engagement' | 'wedding' | 'funeral' | 'baptism';
export type ImagePosition = 'top' | 'center' | 'bottom';

export interface BuilderData {
  photoDataUrl: string;
  customMessage: string;
  imagePosition: ImagePosition;
  themeIndex: number;
  hasPhoto: boolean;
}

interface AnnouncementBuilderProps {
  type: AnnouncementType;
  name1?: string;
  familyName1?: string;
  name2?: string;
  familyName2?: string;
  eventDate?: string;
  eventLocation?: string;
  birthDate?: string;
  deathDate?: string;
  baptismDate?: string;
  onChange?: (data: BuilderData) => void;
}

const ENGAGEMENT_THEMES = [
  { name: 'Rose Gold', cardBg: 'linear-gradient(145deg, #fff1f5 0%, #fff7ed 100%)', borderColor: '#f3a8c0', titleColor: '#9d174d', nameColor: '#7c2d12', accentColor: '#b45309', frameBg: '#fce7f3', churchColor: '#9d174d' },
  { name: 'Pearl White', cardBg: 'linear-gradient(145deg, #fafafa 0%, #f5f5f5 100%)', borderColor: '#d4af37', titleColor: '#374151', nameColor: '#111827', accentColor: '#d4af37', frameBg: '#f9fafb', churchColor: '#4b5563' },
  { name: 'Burgundy', cardBg: 'linear-gradient(145deg, #fff8f8 0%, #fef2f2 100%)', borderColor: '#991b1b', titleColor: '#7f1d1d', nameColor: '#450a0a', accentColor: '#991b1b', frameBg: '#fee2e2', churchColor: '#7f1d1d' },
];

const WEDDING_THEMES = [
  { name: 'Ivory Gold', cardBg: 'linear-gradient(145deg, #fffdf0 0%, #fef9e7 100%)', borderColor: '#d4af37', titleColor: '#78350f', nameColor: '#451a03', accentColor: '#b45309', frameBg: '#fef3c7', churchColor: '#92400e' },
  { name: 'Sage Green', cardBg: 'linear-gradient(145deg, #f0fdf4 0%, #f7fee7 100%)', borderColor: '#6b7280', titleColor: '#14532d', nameColor: '#052e16', accentColor: '#4d7c0f', frameBg: '#dcfce7', churchColor: '#166534' },
  { name: 'Classic White', cardBg: 'linear-gradient(145deg, #ffffff 0%, #f9fafb 100%)', borderColor: '#9ca3af', titleColor: '#1f2937', nameColor: '#111827', accentColor: '#374151', frameBg: '#f3f4f6', churchColor: '#4b5563' },
];

const FUNERAL_THEMES = [
  { name: 'Solemn Grey', cardBg: 'linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)', borderColor: '#475569', titleColor: '#1e293b', nameColor: '#0f172a', accentColor: '#475569', frameBg: '#cbd5e1', churchColor: '#334155' },
  { name: 'Dark Navy', cardBg: 'linear-gradient(145deg, #1e3a5f 0%, #0f2744 100%)', borderColor: '#93c5fd', titleColor: '#e0f2fe', nameColor: '#f0f9ff', accentColor: '#93c5fd', frameBg: '#1e40af', churchColor: '#bfdbfe' },
  { name: 'Warm Sepia', cardBg: 'linear-gradient(145deg, #fdf8f0 0%, #f5ebe0 100%)', borderColor: '#78350f', titleColor: '#451a03', nameColor: '#292524', accentColor: '#7c2d12', frameBg: '#fed7aa', churchColor: '#78350f' },
];

const BAPTISM_THEMES = [
  { name: 'Heavenly Blue', cardBg: 'linear-gradient(145deg, #f0f9ff 0%, #e0f2fe 100%)', borderColor: '#7dd3fc', titleColor: '#0c4a6e', nameColor: '#075985', accentColor: '#0369a1', frameBg: '#bae6fd', churchColor: '#0369a1' },
  { name: 'Pure White', cardBg: 'linear-gradient(145deg, #fafafa 0%, #f5f0eb 100%)', borderColor: '#d4af37', titleColor: '#374151', nameColor: '#111827', accentColor: '#b45309', frameBg: '#fef9c3', churchColor: '#4b5563' },
  { name: 'Soft Lavender', cardBg: 'linear-gradient(145deg, #faf5ff 0%, #ede9fe 100%)', borderColor: '#c4b5fd', titleColor: '#4c1d95', nameColor: '#3b0764', accentColor: '#7c3aed', frameBg: '#ddd6fe', churchColor: '#5b21b6' },
];

function getThemes(type: AnnouncementType) {
  if (type === 'engagement') return ENGAGEMENT_THEMES;
  if (type === 'wedding') return WEDDING_THEMES;
  if (type === 'baptism') return BAPTISM_THEMES;
  return FUNERAL_THEMES;
}

interface TemplateCardProps {
  type: AnnouncementType;
  theme: (typeof ENGAGEMENT_THEMES)[0];
  photoUrl: string;
  imagePosition: ImagePosition;
  name1: string;
  familyName1: string;
  name2: string;
  familyName2: string;
  eventDate: string;
  eventLocation: string;
  birthDate: string;
  deathDate: string;
  customMessage: string;
  baptismDate?: string;
}

function TemplateCard({ type, theme, photoUrl, imagePosition, name1, familyName1, name2, familyName2, eventDate, eventLocation, birthDate, deathDate, customMessage, baptismDate }: TemplateCardProps) {
  const positionMap: Record<ImagePosition, string> = { top: '20% center', center: 'center center', bottom: '80% center' };
  const objPosition = positionMap[imagePosition];

  if (type === 'engagement') {
    return (
      <div style={{ background: theme.cardBg, border: `2px solid ${theme.borderColor}`, width: 300, height: 420, position: 'relative', fontFamily: '"Cormorant Garamond", Georgia, serif', overflow: 'hidden', borderRadius: 12, flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.6) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ padding: '18px 20px 14px', textAlign: 'center', borderBottom: `1px solid ${theme.borderColor}` }}>
          <div style={{ fontSize: 18, color: theme.titleColor, marginBottom: 2 }}>✝</div>
          <div style={{ fontSize: 8, letterSpacing: 3, textTransform: 'uppercase', color: theme.accentColor, fontFamily: 'Georgia, serif' }}>Saint Mina & Pope Cyril VI</div>
        </div>
        <div style={{ textAlign: 'center', padding: '10px 20px 4px' }}>
          <div style={{ fontSize: 9, letterSpacing: 2.5, textTransform: 'uppercase', color: theme.accentColor }}>Engagement Announcement</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
          <div style={{ width: 88, height: 88, borderRadius: '50%', border: `3px solid ${theme.borderColor}`, overflow: 'hidden', background: theme.frameBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {photoUrl
              ? <img src={photoUrl} alt="couple" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: objPosition }} />
              : <div style={{ color: theme.accentColor, opacity: 0.5, fontSize: 28 }}>♡</div>
            }
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, padding: '0 16px' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: theme.nameColor }}>{name1 || 'Bridegroom Name'}</div>
          <div style={{ fontSize: 10, color: theme.accentColor, letterSpacing: 1 }}>{familyName1 || 'Family Name'}</div>
          <div style={{ fontSize: 20, color: theme.borderColor, margin: '3px 0', lineHeight: 1 }}>&</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: theme.nameColor }}>{name2 || 'Bride Name'}</div>
          <div style={{ fontSize: 10, color: theme.accentColor, letterSpacing: 1 }}>{familyName2 || 'Family Name'}</div>
        </div>
        <div style={{ margin: '10px 20px 0', borderTop: `1px solid ${theme.borderColor}`, paddingTop: 8, textAlign: 'center' }}>
          {eventDate && <div style={{ fontSize: 9, color: theme.accentColor, letterSpacing: 1.5, marginBottom: 2 }}>{eventDate}</div>}
          {eventLocation && <div style={{ fontSize: 8, color: theme.titleColor, opacity: 0.7 }}>{eventLocation}</div>}
        </div>
        {customMessage && (
          <div style={{ margin: '8px 20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: theme.titleColor, fontStyle: 'italic', opacity: 0.8, lineHeight: 1.4 }}>&quot;{customMessage}&quot;</div>
          </div>
        )}
        <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 7.5, letterSpacing: 1, color: theme.churchColor, opacity: 0.7, textTransform: 'uppercase' }}>Saint Mina & Pope Cyril VI Coptic Orthodox Church</div>
        </div>
      </div>
    );
  }

  if (type === 'wedding') {
    return (
      <div style={{ background: theme.cardBg, border: `2px solid ${theme.borderColor}`, width: 300, height: 420, position: 'relative', fontFamily: '"Cormorant Garamond", Georgia, serif', overflow: 'hidden', borderRadius: 12, flexShrink: 0 }}>
        <div style={{ padding: '14px 20px 10px', textAlign: 'center', borderBottom: `1px solid ${theme.borderColor}` }}>
          <div style={{ fontSize: 16, color: theme.titleColor, marginBottom: 1 }}>✝</div>
          <div style={{ fontSize: 8, letterSpacing: 3, textTransform: 'uppercase', color: theme.accentColor }}>Wedding Announcement</div>
          <div style={{ fontSize: 7, letterSpacing: 1.5, color: theme.churchColor, opacity: 0.7, marginTop: 1 }}>Saint Mina & Pope Cyril VI COC</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <div style={{ width: 120, height: 88, borderRadius: 10, border: `3px solid ${theme.borderColor}`, overflow: 'hidden', background: theme.frameBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {photoUrl
              ? <img src={photoUrl} alt="couple" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: objPosition }} />
              : <div style={{ color: theme.accentColor, opacity: 0.4, fontSize: 24 }}>☩</div>
            }
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 12, padding: '0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: theme.nameColor }}>{name1 || 'Groom'}</div>
              <div style={{ fontSize: 9, color: theme.accentColor, letterSpacing: 1 }}>{familyName1 || 'Family'}</div>
            </div>
            <div style={{ fontSize: 22, color: theme.borderColor, lineHeight: 1 }}>&</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: theme.nameColor }}>{name2 || 'Bride'}</div>
              <div style={{ fontSize: 9, color: theme.accentColor, letterSpacing: 1 }}>{familyName2 || 'Family'}</div>
            </div>
          </div>
        </div>
        <div style={{ margin: '12px 20px 0', borderTop: `1px solid ${theme.borderColor}`, paddingTop: 8, textAlign: 'center' }}>
          {eventDate && <div style={{ fontSize: 9, color: theme.accentColor, letterSpacing: 1.5, marginBottom: 2 }}>{eventDate}</div>}
          {eventLocation && <div style={{ fontSize: 8, color: theme.titleColor, opacity: 0.7 }}>{eventLocation}</div>}
          <div style={{ fontSize: 9, fontStyle: 'italic', color: theme.titleColor, opacity: 0.65, marginTop: 6, lineHeight: 1.4 }}>
            {customMessage || '"What God has joined together, let no one separate." — Mark 10:9'}
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 7.5, letterSpacing: 1, color: theme.churchColor, opacity: 0.7, textTransform: 'uppercase' }}>Blessed is this Holy Union</div>
        </div>
      </div>
    );
  }

  if (type === 'baptism') {
    return (
      <div style={{ background: theme.cardBg, border: `2px solid ${theme.borderColor}`, width: 300, height: 420, position: 'relative', fontFamily: '"Cormorant Garamond", Georgia, serif', overflow: 'hidden', borderRadius: 12, flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.6) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ padding: '14px 20px 10px', textAlign: 'center', borderBottom: `1px solid ${theme.borderColor}` }}>
          <div style={{ fontSize: 18, color: theme.titleColor, marginBottom: 1 }}>✝</div>
          <div style={{ fontSize: 8, letterSpacing: 3, textTransform: 'uppercase', color: theme.accentColor }}>Holy Baptism</div>
          <div style={{ fontSize: 7, letterSpacing: 1.5, color: theme.churchColor, opacity: 0.7, marginTop: 1 }}>Saint Mina & Pope Cyril VI COC</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
          <div style={{ width: 88, height: 88, borderRadius: '50%', border: `3px solid ${theme.borderColor}`, overflow: 'hidden', background: theme.frameBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {photoUrl
              ? <img src={photoUrl} alt="child" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: imagePosition === 'top' ? 'top' : imagePosition === 'bottom' ? 'bottom' : 'center' }} />
              : <div style={{ color: theme.accentColor, opacity: 0.5, fontSize: 26 }}>✝</div>
            }
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, padding: '0 16px' }}>
          <div style={{ fontSize: 8, letterSpacing: 2, textTransform: 'uppercase', color: theme.accentColor, marginBottom: 4 }}>Rejoice with us!</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: theme.nameColor, lineHeight: 1.2 }}>{name1 || 'Child\'s Name'}</div>
          <div style={{ fontSize: 10, color: theme.accentColor, letterSpacing: 1, marginTop: 2 }}>{familyName1 || 'Family Name'}</div>
          {name2 && <div style={{ fontSize: 9, color: theme.titleColor, opacity: 0.7, marginTop: 4 }}>Child of {name2}</div>}
        </div>
        <div style={{ margin: '10px 20px 0', borderTop: `1px solid ${theme.borderColor}`, paddingTop: 8, textAlign: 'center' }}>
          {baptismDate && <div style={{ fontSize: 9, color: theme.accentColor, letterSpacing: 1.5, marginBottom: 2 }}>{baptismDate}</div>}
          {eventLocation && <div style={{ fontSize: 8, color: theme.titleColor, opacity: 0.7 }}>{eventLocation}</div>}
          <div style={{ fontSize: 9, fontStyle: 'italic', color: theme.titleColor, opacity: 0.75, marginTop: 5, lineHeight: 1.4 }}>
            {customMessage || '"Baptizing them in the name of the Father, Son & Holy Spirit" — Matt 28:19'}
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 7.5, letterSpacing: 1, color: theme.churchColor, opacity: 0.7, textTransform: 'uppercase' }}>Blessed is this New Child of God</div>
        </div>
      </div>
    );
  }

  // Funeral template
  return (
    <div style={{ background: theme.cardBg, border: `2px solid ${theme.borderColor}`, width: 300, height: 420, position: 'relative', fontFamily: '"Cormorant Garamond", Georgia, serif', overflow: 'hidden', borderRadius: 12, flexShrink: 0 }}>
      <div style={{ padding: '14px 20px 10px', textAlign: 'center', borderBottom: `1px solid ${theme.borderColor}` }}>
        <div style={{ fontSize: 16, color: theme.titleColor }}>✝</div>
        <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: theme.accentColor, marginTop: 2 }}>In Loving Memory of</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
        <div style={{ width: 84, height: 104, borderRadius: '50% / 40%', border: `3px solid ${theme.borderColor}`, overflow: 'hidden', background: theme.frameBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {photoUrl
            ? <img src={photoUrl} alt="deceased" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: objPosition }} />
            : <div style={{ color: theme.accentColor, opacity: 0.4, fontSize: 22 }}>✝</div>
          }
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 10, padding: '0 20px' }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: theme.nameColor, lineHeight: 1.2 }}>
          {name1 || 'First Name'} {familyName1 || 'Last Name'}
        </div>
        {(birthDate || deathDate) && (
          <div style={{ fontSize: 9, color: theme.accentColor, letterSpacing: 1.5, marginTop: 5 }}>
            {birthDate || '—'} · {deathDate || '—'}
          </div>
        )}
      </div>
      <div style={{ margin: '12px 20px 0', borderTop: `1px solid ${theme.borderColor}`, paddingTop: 8, textAlign: 'center' }}>
        <div style={{ fontSize: 9, fontStyle: 'italic', color: theme.titleColor, opacity: 0.75, lineHeight: 1.5 }}>
          {customMessage || '"Blessed are those who mourn, for they shall be comforted." — Matthew 5:4'}
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center' }}>
        <div style={{ fontSize: 9, letterSpacing: 1.5, color: theme.churchColor, textTransform: 'uppercase', marginBottom: 2 }}>May Their Memory Be Eternal</div>
        <div style={{ fontSize: 7, color: theme.churchColor, opacity: 0.6, letterSpacing: 0.8 }}>Saint Mina & Pope Cyril VI Coptic Orthodox Church</div>
      </div>
    </div>
  );
}

export default function AnnouncementBuilder({ type, name1 = '', familyName1 = '', name2 = '', familyName2 = '', eventDate = '', eventLocation = '', birthDate = '', deathDate = '', baptismDate = '', onChange }: AnnouncementBuilderProps) {
  const [photoUrl, setPhotoUrl] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [imagePosition, setImagePosition] = useState<ImagePosition>('center');
  const [themeIndex, setThemeIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const themes = getThemes(type);

  const typeLabels: Record<AnnouncementType, { title: string; photoLabel: string; msgPlaceholder: string }> = {
    engagement: { title: 'Engagement Announcement Preview', photoLabel: 'Upload couple photo', msgPlaceholder: 'e.g. "He who finds a wife finds a good thing..." — Proverbs 18:22' },
    wedding: { title: 'Wedding Announcement Preview', photoLabel: 'Upload couple photo', msgPlaceholder: 'e.g. "What God has joined together, let no one separate." — Mark 10:9' },
    funeral: { title: 'Memorial Announcement Preview', photoLabel: 'Upload photo of the deceased', msgPlaceholder: 'e.g. "Blessed are those who mourn, for they shall be comforted." — Matthew 5:4' },
    baptism: { title: 'Baptism Announcement Preview', photoLabel: 'Upload a photo of the child', msgPlaceholder: 'e.g. "Baptizing them in the name of the Father and of the Son and of the Holy Spirit." — Matthew 28:19' },
  };
  const labels = typeLabels[type];

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setPhotoUrl(url);
      onChange?.({ photoDataUrl: url, customMessage, imagePosition, themeIndex, hasPhoto: true });
    };
    reader.readAsDataURL(file);
  }, [customMessage, imagePosition, themeIndex, onChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) processFile(e.target.files[0]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  };

  const updateData = useCallback((updates: Partial<BuilderData>) => {
    const next = { photoDataUrl: photoUrl, customMessage, imagePosition, themeIndex, hasPhoto: !!photoUrl, ...updates };
    onChange?.(next);
  }, [photoUrl, customMessage, imagePosition, themeIndex, onChange]);

  const accentClass = type === 'funeral' ? 'focus:ring-slate-400' : 'focus:ring-gold-500';

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className={`px-8 py-5 border-b border-gray-200 ${type === 'funeral' ? 'bg-slate-50' : 'bg-teal-50'}`}>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
          <i className={`ri-layout-masonry-line ${type === 'funeral' ? 'text-slate-600' : 'text-teal-600'}`}></i>
          {labels.title}
        </h2>
        <p className="text-sm text-gray-500 mt-1">Your information is placed on the template automatically as you fill out the form above. Upload a photo and add a personal message below.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-0">
        {/* Controls Panel */}
        <div className="lg:w-80 border-r border-gray-200 p-6 space-y-6 flex-shrink-0">
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{labels.photoLabel}</label>
            <div
              className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${isDragging ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-gray-400'}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              {photoUrl ? (
                <div className="flex flex-col items-center gap-2">
                  <img src={photoUrl} alt="preview" className="w-20 h-20 object-cover rounded-lg mx-auto" style={{ objectPosition: imagePosition === 'top' ? 'top' : imagePosition === 'bottom' ? 'bottom' : 'center' }} />
                  <span className="text-xs text-gray-500">Click to change photo</span>
                </div>
              ) : (
                <div className="py-2">
                  <i className="ri-image-add-line text-2xl text-gray-400"></i>
                  <p className="text-xs text-gray-500 mt-1">Click or drag & drop</p>
                  <p className="text-xs text-gray-400">JPG, PNG — max 10MB</p>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" name="announcementPhoto" />
            </div>
          </div>

          {/* Image Position — only visible when photo uploaded */}
          {photoUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Photo Focus</label>
              <div className="flex gap-2">
                {(['top', 'center', 'bottom'] as ImagePosition[]).map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => { setImagePosition(pos); updateData({ imagePosition: pos }); }}
                    className={`flex-1 py-1.5 text-xs rounded-md border font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${imagePosition === pos ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                  >
                    {pos}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Personal Message / Inscription <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              value={customMessage}
              onChange={(e) => { setCustomMessage(e.target.value); updateData({ customMessage: e.target.value }); }}
              maxLength={200}
              rows={3}
              placeholder={labels.msgPlaceholder}
              className={`w-full text-sm px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 ${accentClass} focus:border-transparent`}
              name="announcementMessage"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{customMessage.length}/200</p>
          </div>

          {/* Color Theme */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color Theme</label>
            <div className="space-y-2">
              {themes.map((t, i) => (
                <label key={i} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="announcementTheme"
                    checked={themeIndex === i}
                    onChange={() => { setThemeIndex(i); updateData({ themeIndex: i }); }}
                    className="w-4 h-4 cursor-pointer"
                  />
                  <div className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0" style={{ background: t.cardBg }} />
                  <span className="text-sm text-gray-700">{t.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Remove photo */}
          {photoUrl && (
            <button
              type="button"
              onClick={() => { setPhotoUrl(''); updateData({ photoDataUrl: '', hasPhoto: false }); }}
              className="text-xs text-red-500 hover:text-red-700 cursor-pointer transition-colors"
            >
              <i className="ri-delete-bin-line mr-1"></i>Remove photo
            </button>
          )}
        </div>

        {/* Preview Panel */}
        <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center py-10 px-6 min-h-[480px]">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-6">Live Preview</p>
          <TemplateCard
            type={type}
            theme={themes[themeIndex]}
            photoUrl={photoUrl}
            imagePosition={imagePosition}
            name1={name1}
            familyName1={familyName1}
            name2={name2}
            familyName2={familyName2}
            eventDate={eventDate}
            eventLocation={eventLocation}
            birthDate={birthDate}
            deathDate={deathDate}
            customMessage={customMessage}
            baptismDate={baptismDate}
          />
          <p className="text-xs text-gray-400 mt-5 text-center max-w-xs">This preview updates in real time. The final announcement will be prepared by the church office.</p>
        </div>
      </div>
    </div>
  );
}
